import { Portal } from '@reach/portal';
import cl from 'classnames';
import * as React from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useSlate } from 'slate-react';
import styled from 'styled-components';

import { isCollapsed } from '../../../../slatePlugins/common/queries/isCollapsed';
import { BalloonToolbarProps } from '../../../../slatePlugins/components/Toolbar/BalloonToolbar/BalloonToolbar.types';
import { setPositionAtSelection } from '../../../../slatePlugins/components/Toolbar/BalloonToolbar/setPositionAtSelection';
import { getNodeAtEdge } from '../../helpers/node';
import { isAtEndOf, isAtStartOf } from '../../helpers/selection';
import { BallonToolbarMain } from '../balloon-toolbar-on-expanded/BalloonToolbarOnExpanded';
import { StyledToolbar } from '../StyledToolbar';
import { ItemToolbarContext } from './withItemContextToolbar';

export interface BalloonToolbarAtEdgeBlockProps extends BalloonToolbarProps {
  type: string;
}

const ToolbarWrapper = styled.div`
  &.hidden {
    pointer-events: none;
    opacity: 0;
  }
`;
const BalloonToolbarAtEdgeBlockBase = ({
  className,
  children,
  direction = 'top',
  theme = 'dark',
  arrow = false,
  type,
}: BalloonToolbarAtEdgeBlockProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isFocused, setFocus] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const editor = useSlate();
  const { selection } = editor;
  let hidden = true;
  if (isFocused) {
    hidden = false;
  } else if (isCollapsed(selection)) {
    hidden = !(isAtStartOf(editor as any, type) || isAtEndOf(editor as any, type));
  }

  React.useEffect(() => {
    if (!hidden && !isFocused) {
      setSelectedItem(getNodeAtEdge(editor, type));
      setPositionAtSelection(ref.current, direction);
    }
  }, [direction, editor, hidden, isFocused, selection, type]);

  return (
    <ItemToolbarContext.Provider
      value={{
        selectedItem,
        setSelectedItem,
        isToolbarFocus: isFocused,
      }}
    >
      <Portal>
        <BallonToolbarMain className={cl({ hidden })}>
          <OutsideClickHandler
            onOutsideClick={() => {
              setFocus(false);
            }}
          >
            <ToolbarWrapper
              className={cl({ hidden })}
              onMouseDown={() => {
                setFocus(true);
              }}
            >
              <StyledToolbar
                ref={ref}
                className={cl(className, 'dol-balloon-toolbar')}
                $hidden={hidden}
                $theme={theme}
                $arrow={arrow}
              >
                {children}
              </StyledToolbar>
            </ToolbarWrapper>
          </OutsideClickHandler>
        </BallonToolbarMain>
      </Portal>
    </ItemToolbarContext.Provider>
  );
};

export const BalloonToolbarAtEdgeBlock = React.memo(BalloonToolbarAtEdgeBlockBase);
